// CreditNova Workflow Context
// Manages multi-step workflow state across the analysis process
import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type {
  FinancialData,
  CreditReportExtractedData,
  SelectedCreditReportItems,
  AccountData,
  AnyBankStatementFindings,
  SummaryPreferences,
  CustomRatio,
  WriteupType,
  WorkflowState,
} from '@creditnova/shared/schemas';
import { setWorkflowId } from '@/lib/api-client';
import { api } from '@/lib/api-client';
import { applyTemplateToWorkflow } from '@/lib/template-utils';
import { Template } from '@creditnova/shared';

// SSE API base URL
const API_URL = import.meta.env.VITE_API_URL || '';

// Per-document status tracked via SSE status events
export interface DocumentStatus {
  id: string;
  name: string;
  documentType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  integrityReport?: {
    verdict: 'clean' | 'warning' | 'alert';
    avgConfidence: number;
    flags: Array<{ category: string; severity: string; message: string; field?: string }>;
  };
}

// Activity log entry from SSE activity events
export interface ActivityEntry {
  id: string;
  timestamp: string;
  source: string;
  message: string;
  level: 'info' | 'success' | 'warning' | 'error';
}

// ===========================================
// Types
// ===========================================

// ---------------------------------------------------------------
// WorkflowStep — Identifies each discrete page in the workflow.
//
// Flow: summary → upload → overrides → metrics → generating → report
// ---------------------------------------------------------------
export type WorkflowStep =
  | 'summary'
  | 'upload'         // Unified multi-document upload page
  | 'overrides'      // Tabbed financial statement overrides (concurrent flow)
  | 'metrics'
  | 'generating'     // Auto-generating report (concurrent flow terminal step)
  | 'report';

export interface WorkflowStepConfig {
  id: WorkflowStep;
  name: string;
  shortName: string;
  path: string;
  required: boolean;
}

export interface WorkflowData {
  workflowId: string;
  analysisId?: string; // Reserved analysisId for the workflow (created on upload page)
  // Basic info
  businessName: string;
  writeupType: WriteupType;
  dealText: string;

  // Document IDs
  financialUploadId?: string;
  creditReportUploadId?: string;
  bankStatementUploadId?: string;
  corporateUploadId?: string;

  // Extracted data
  financialData: FinancialData | null;
  creditReportData: CreditReportExtractedData | null;
  selectedCreditReportItems: SelectedCreditReportItems | null;
  accountData: AccountData | null;
  bankStatementFindings: AnyBankStatementFindings | null;

  // Analysis config
  selectedMetrics: string[];
  customRatios: CustomRatio[];
  offBalanceSheetRecords: Record<string, unknown>;
  summaryPreferences: SummaryPreferences | null;
  creditReportInclusionMode?: 'automatic' | 'manual';

  // Report ID (once generated)
  reportId?: string;
  templateId?: string | null;

  // ---------------------------------------------------------------
  // Concurrent Workflow — New fields for the unified upload flow
  // ---------------------------------------------------------------

  // uploadedDocuments: Client-side record of every file submitted via the
  // batch upload endpoint. Each entry stores the documentType, period label,
  // display label, and the server-assigned document ID. This array drives
  // the processing dashboard's initial state before polling takes over.
  uploadedDocuments: Array<{
    id: string;            // Server-assigned document ID (UUID)
    documentType: string;  // 'financial_statement' | 'bank_statement' | 'credit_report' | 'corporate_search'
    period: string;        // User-provided period label (e.g., "Jan 2025")
    label: string;         // Display label (defaults to filename)
    fileName: string;      // Original filename for display
  }>;

  // workflowState: High-level state machine for the concurrent flow.
  //   'idle'                — No upload initiated yet
  //   'uploading'           — Files are being sent to the server
  //   'processing'          — BullMQ jobs are running (polling active)
  //   'financial_review'    — Financial extraction done, awaiting user overrides
  //   'metrics_review'      — Overrides confirmed, awaiting user metrics selection
  //   'processing_remaining'— User completed overrides/metrics, other jobs still running
  //   'generating'          — All extractions done, report generation in progress
  //   'complete'            — Report generated successfully
  workflowState: WorkflowState;

  // ---------------------------------------------------------------
  // Multi-document financial extractions
  //
  // When multiple financial statements are uploaded, each gets its own
  // extraction. These arrays track the per-document extractions through
  // the override flow:
  //   1. financialExtractions — Raw OpenAI extractions (before overrides)
  //   2. overriddenFinancialExtractions — After user confirms overrides
  // Both arrays are parallel — same index = same document.
  // ---------------------------------------------------------------
  financialExtractions: Array<{
    documentId: string;       // Server document ID
    period: string;           // Period label (e.g., "FY 2024")
    fileName: string;         // Original filename
    data: FinancialData;      // Extracted financial data
  }>;
  overriddenFinancialExtractions: Array<{
    documentId: string;
    period: string;
    fileName: string;
    data: FinancialData;      // Financial data with user overrides applied
  }>;

  // ---------------------------------------------------------------
  // Non-financial extraction results (pre-computed while user reviews FS overrides)
  //
  // These are populated by the early extraction effect in upload.tsx
  // when non-FS worker jobs complete while the user is on the
  // overrides/metrics page. extractRemainingAndGenerateReport() skips
  // re-extracting docs whose results are already here.
  // ---------------------------------------------------------------
  earlyExtractions: Array<{
    documentType: string;
    documentId: string;
    period: string;
    data: any;
  }>;

  // ---------------------------------------------------------------
  // Cross-period analysis results
  //
  // Populated after all extractions are complete and cross-period
  // analysis runs for each document type with >1 document.
  // These feed into the report generation agent pipeline.
  // Typed as Record<string, unknown> since the specific shapes differ
  // per doc type — the backend schemas are authoritative.
  // ---------------------------------------------------------------
  crossPeriodAnalysis: {
    financial: Record<string, unknown> | null;
    creditReport: Record<string, unknown> | null;
    bankStatement: Record<string, unknown> | null;
    corporate: Record<string, unknown> | null;
  };
}

export interface WorkflowContextType {
  // Current step
  currentStep: WorkflowStep;
  setCurrentStep: (step: WorkflowStep) => void;

  // Available steps based on preferences
  availableSteps: WorkflowStepConfig[];
  stepIndex: number;

  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: WorkflowStep) => void;
  canGoNext: boolean;
  canGoPrevious: boolean;

  // Data
  data: WorkflowData;
  updateData: (updates: Partial<WorkflowData>) => void;
  resetWorkflow: () => void;

  // Template support
  loadTemplate: (template: Template) => void;

  // Status
  isStepComplete: (step: WorkflowStep) => boolean;
  completedSteps: Set<WorkflowStep>;
  markStepComplete: (step: WorkflowStep) => void;

  // SSE-driven document statuses (persists across route navigation)
  documentStatuses: DocumentStatus[];
  // Set document statuses on resume (populate from previous session)
  setResumedDocumentStatuses: (statuses: DocumentStatus[]) => void;
  // Activity log entries (persists across route navigation)
  activityLog: ActivityEntry[];
  // Add an activity log entry
  addActivity: (source: string, message: string, level: ActivityEntry['level']) => void;
  // Close the SSE connection (called when report generation completes)
  closeSse: () => void;
  // Wait for all in-flight early extractions to complete, returns the
  // extraction results directly from the ref (avoids stale React state closures).
  waitForEarlyExtractions: () => Promise<Array<{ documentType: string; documentId: string; period: string; data: any }>>;
  // Report result delivered via SSE 'report-result' event
  reportResult: { analysisId: string; reportId: string } | null;
  // Report error delivered via SSE 'report-result' event (failed)
  reportError: string | null;
}

// ===========================================
// Constants
// ===========================================

// ---------------------------------------------------------------
// ALL_STEPS — Master list of every possible workflow step.
//
// The 'upload' step is the unified drag-and-drop page where users
// submit ALL documents at once.
// ---------------------------------------------------------------
const ALL_STEPS: WorkflowStepConfig[] = [
  { id: 'summary', name: 'Summary Design', shortName: 'Summary', path: '/workflow/summary', required: true },
  { id: 'upload', name: 'Document Upload', shortName: 'Upload', path: '/workflow/upload', required: false },
  { id: 'overrides', name: 'Financial Review', shortName: 'Review', path: '/workflow/overrides', required: false },
  { id: 'metrics', name: 'Metrics Selection', shortName: 'Metrics', path: '/workflow/metrics', required: false },
  { id: 'generating', name: 'Generating Report', shortName: 'Generating', path: '/workflow/generate', required: false },
  { id: 'report', name: 'Final Report', shortName: 'Report', path: '/workflow/report', required: true },
];

const getInitialData = (): WorkflowData => ({
  workflowId: '',
  templateId: null,
  businessName: '',
  writeupType: 'full_analysis',
  dealText: '',
  financialData: null,
  creditReportData: null,
  selectedCreditReportItems: null,
  accountData: null,
  bankStatementFindings: null,
  selectedMetrics: [],
  customRatios: [],
  offBalanceSheetRecords: {},
  summaryPreferences: null,
  // Concurrent workflow defaults
  uploadedDocuments: [],
  workflowState: 'idle',
  financialExtractions: [],
  overriddenFinancialExtractions: [],
  earlyExtractions: [],
  crossPeriodAnalysis: {
    financial: null,
    creditReport: null,
    bankStatement: null,
    corporate: null,
  },
});

// Use sessionStorage (per-tab) so multiple browser tabs can run
// independent workflows without interfering with each other.
const STORAGE_KEY = 'creditnova_workflow_data';
const STEP_STORAGE_KEY = 'creditnova_workflow_step';
const COMPLETED_STORAGE_KEY = 'creditnova_workflow_completed';
const storage = typeof window !== 'undefined' ? window.sessionStorage : null;

// ===========================================
// Context
// ===========================================

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

// ===========================================
// Provider
// ===========================================

interface WorkflowProviderProps {
  children: ReactNode;
}

export function WorkflowProvider({ children }: WorkflowProviderProps) {
  const navigate = useNavigate();

  // Load initial state from sessionStorage (per-tab)
  const [currentStep, setCurrentStepState] = useState<WorkflowStep>(() => {
    if (typeof window === 'undefined') return 'summary';
    const saved = storage?.getItem(STEP_STORAGE_KEY);
    return (saved as WorkflowStep) || 'summary';
  });

  const [data, setData] = useState<WorkflowData>(() => {
      const fresh = getInitialData();
      if (typeof window === 'undefined') return fresh;
      const saved = storage?.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return { ...fresh, ...JSON.parse(saved) };
        } catch {
          return fresh;
        }
      }
      return fresh;
    });

  const [completedSteps, setCompletedSteps] = useState<Set<WorkflowStep>>(() => {
    if (typeof window === 'undefined') return new Set();
    const saved = storage?.getItem(COMPLETED_STORAGE_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // ---------------------------------------------------------------
  // SSE + Early Extraction State (persists across route navigation)
  // NOT persisted to localStorage — repopulated from SSE on reconnect.
  // ---------------------------------------------------------------
  const [documentStatuses, setDocumentStatuses] = useState<DocumentStatus[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityEntry[]>([]);
  const [reportResult, setReportResult] = useState<{ analysisId: string; reportId: string } | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  // SSE refs
  const eventSourceRef = useRef<EventSource | null>(null);
  const activityIdRef = useRef(0);
  // Early extraction refs — hydrated from persisted earlyExtractions
  const earlyExtractedRef = useRef<Set<string>>(
    new Set((data.earlyExtractions || []).map((e: any) => e.documentId))
  );
  const earlyExtractionsRef = useRef<Array<{ documentType: string; documentId: string; period: string; data: any }>>(
    data.earlyExtractions || []
  );
  const earlyExtractionDoneRef = useRef<Promise<void>>(Promise.resolve());
  // Track whether SSE is currently connecting to prevent duplicates
  const sseConnectingRef = useRef(false);

  const addActivity = useCallback((source: string, message: string, level: ActivityEntry['level'] = 'info') => {
    const entry: ActivityEntry = {
      id: String(++activityIdRef.current),
      timestamp: new Date().toISOString(),
      source,
      message,
      level,
    };
    setActivityLog((prev) => [...prev, entry]);
  }, []);

  // ---------------------------------------------------------------
  // SSE Connection — Stays alive across route navigation
  //
  // Connects when docs are uploaded and no report exists yet.
  // Updates documentStatuses and activityLog from server events.
  // ---------------------------------------------------------------
  useEffect(() => {
    const shouldConnect =
      data.uploadedDocuments.length > 0 &&
      !!data.workflowId &&
      !data.reportId &&
      ['uploading', 'processing', 'financial_review', 'metrics_review', 'processing_remaining', 'generating'].includes(data.workflowState);

    if (!shouldConnect) return;
    // Don't reconnect if already connected
    if (eventSourceRef.current) return;
    if (sseConnectingRef.current) return;

    sseConnectingRef.current = true;
    let cancelled = false;
    let es: EventSource | null = null;

    (async () => {
      let ticketParam = '';
      try {
        const { ticket } = await api.documents.sseTicket();
        ticketParam = `&ticket=${encodeURIComponent(ticket)}`;
      } catch (err) {
        console.error('Failed to obtain SSE ticket:', err);
      }
      if (cancelled) { sseConnectingRef.current = false; return; }
      const url = `${API_URL}/api/documents/workflow-stream?workflowId=${data.workflowId}${ticketParam}`;
      es = new EventSource(url);
      eventSourceRef.current = es;
      sseConnectingRef.current = false;

      es.onmessage = (event) => {
        try {
          const evt = JSON.parse(event.data);

          if (evt.type === 'status') {
            setDocumentStatuses((prev) => {
              const existing = prev.find((d) => d.id === evt.documentId);
              if (existing) {
                return prev.map((d) =>
                  d.id === evt.documentId
                    ? {
                        ...d,
                        status: evt.status,
                        name: evt.documentName || d.name,
                        ...(evt.integrityReport ? { integrityReport: evt.integrityReport } : {}),
                      }
                    : d
                );
              }
              return [...prev, {
                id: evt.documentId,
                name: evt.documentName || evt.documentId,
                documentType: evt.documentType || 'other',
                status: evt.status || 'pending',
                ...(evt.integrityReport ? { integrityReport: evt.integrityReport } : {}),
              }];
            });
          }

          if (evt.type === 'activity') {
            const entry: ActivityEntry = {
              id: String(++activityIdRef.current),
              timestamp: evt.timestamp || new Date().toISOString(),
              source: evt.source || 'system',
              message: evt.message || '',
              level: evt.level || 'info',
            };
            setActivityLog((prev) => [...prev, entry]);
          }

          if (evt.type === 'report-result') {
            if (evt.reportStatus === 'completed' && evt.analysisId) {
              setReportResult({ analysisId: evt.analysisId, reportId: evt.reportId });
            } else if (evt.reportStatus === 'failed') {
              setReportError(evt.reportError || evt.message || 'Report generation failed');
            }
          }
        } catch (err) {
          console.warn('Failed to parse SSE event:', err);
        }
      };

      es.onerror = () => {
        console.warn('SSE connection error, will auto-reconnect');
      };
    })();

    return () => {
      cancelled = true;
      sseConnectingRef.current = false;
      if (es) es.close();
      eventSourceRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.uploadedDocuments.length, data.workflowId, data.reportId, data.workflowState]);

  // ---------------------------------------------------------------
  // Early extraction + Phase 1 agents for non-FS docs
  //
  // Runs in context so it survives route navigation (upload.tsx
  // unmounts during overrides/metrics). When non-FS worker jobs
  // complete during financial_review, extracts them and pre-caches
  // Phase 1 agent results in Redis.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (data.workflowState !== 'financial_review' && data.workflowState !== 'metrics_review') return;

    const completedNonFs = documentStatuses.filter(
      (d) =>
        d.documentType !== 'financial_statement' &&
        d.status === 'completed' &&
        !earlyExtractedRef.current.has(d.id)
    );

    if (completedNonFs.length === 0) return;

    for (const doc of completedNonFs) {
      earlyExtractedRef.current.add(doc.id);
    }

    earlyExtractionDoneRef.current = earlyExtractionDoneRef.current.then(async () => {
      for (const doc of completedNonFs) {
        try {
          addActivity('extractor.early', `Early extraction: ${doc.name}…`, 'info');
          const result = await api.extraction.extract({
            uploadId: doc.id,
            workflowId: data.workflowId,
            period: 'annual',
            detailLevel: 'detailed',
          });

          const uploadedDoc = data.uploadedDocuments.find((d) => d.id === doc.id);
          const period = uploadedDoc?.period || 'Annual';

          earlyExtractionsRef.current = [
            ...earlyExtractionsRef.current,
            { documentType: doc.documentType, documentId: doc.id, period, data: result.data },
          ];

          addActivity('extractor.early', `Early extraction complete: ${doc.name}`, 'success');

          // Determine agent type from document type
          const agentTypeMap: Record<string, 'banking-analyst' | 'credit-profile' | 'corporate-legal'> = {
            bank_statement: 'banking-analyst',
            credit_report: 'credit-profile',
            equifax: 'credit-profile',
            corporate_search: 'corporate-legal',
          };
          const agentType = agentTypeMap[doc.documentType];

          if (agentType && result.data) {
            const docTypesForAgent: Record<string, string[]> = {
              'banking-analyst': ['bank_statement'],
              'credit-profile': ['credit_report', 'equifax'],
              'corporate-legal': ['corporate_search'],
            };
            const relevantDocTypes = docTypesForAgent[agentType];

            const totalDocsForAgent = data.uploadedDocuments.filter(
              (d) => relevantDocTypes.includes(d.documentType)
            ).length;

            const extractedForAgent = earlyExtractionsRef.current.filter(
              (e) => relevantDocTypes.includes(e.documentType)
            );

            if (extractedForAgent.length >= totalDocsForAgent) {
              const allExtractions = extractedForAgent.map((e) => ({
                data: e.data,
                period: e.period,
                documentId: e.documentId,
              }));

              const phase1Payload: any = {
                workflowId: data.workflowId,
                agentType,
                writeupType: data.writeupType,
                summaryPreferences: data.summaryPreferences,
                selectedCreditReportItems: null,
              };

              if (agentType === 'banking-analyst') {
                phase1Payload.bankStatementFindings = extractedForAgent[0].data;
                phase1Payload.allBankStatementExtractions = allExtractions;
              } else if (agentType === 'credit-profile') {
                phase1Payload.creditReportData = extractedForAgent[0].data;
                phase1Payload.allCreditReportExtractions = allExtractions;
              } else if (agentType === 'corporate-legal') {
                phase1Payload.accountData = extractedForAgent[0].data;
                phase1Payload.allCorporateExtractions = allExtractions;
              }

              addActivity('phase1.early', `Pre-computing ${agentType} analysis (${allExtractions.length} doc${allExtractions.length > 1 ? 's' : ''})…`, 'info');
              await api.reports.runPhase1(phase1Payload).catch((err: any) => {
                console.warn(`Early Phase 1 failed for ${agentType}:`, err);
                addActivity('phase1.early', `${agentType} pre-compute failed — will retry later`, 'warning');
              });
            }
          }
        } catch (err) {
          console.warn(`Early extraction failed for ${doc.name}:`, err);
          addActivity('extractor.early', `Early extraction failed: ${doc.name} — will retry later`, 'warning');
          earlyExtractedRef.current.delete(doc.id);
        }
      }

      // Sync ref to React state
      setData((prev) => ({ ...prev, earlyExtractions: earlyExtractionsRef.current }));

      // Sync early extractions to backend (fire-and-forget)
      if (data.workflowId && earlyExtractionsRef.current.length > 0) {
        api.workflows.update(data.workflowId, {
          earlyExtractions: earlyExtractionsRef.current,
        }).catch((err) =>
          console.warn('Failed to sync earlyExtractions to backend:', err)
        );
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.workflowState, documentStatuses]);

  const closeSse = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const waitForEarlyExtractions = useCallback(async () => {
    await earlyExtractionDoneRef.current;
    return earlyExtractionsRef.current;
  }, []);

  // Persist state to sessionStorage (per-tab)
  useEffect(() => {
    storage?.setItem(STEP_STORAGE_KEY, currentStep);
  }, [currentStep]);

  useEffect(() => {
    storage?.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    storage?.setItem(COMPLETED_STORAGE_KEY, JSON.stringify([...completedSteps]));
  }, [completedSteps]);

  useEffect(() => {
    if (data.workflowId) {
      setWorkflowId(data.workflowId);
    }
  }, [data.workflowId]);

  // ---------------------------------------------------------------
  // availableSteps — Determines which steps appear in the StageTracker
  // and are navigable.
  //
  // Concurrent flow (new):  summary → upload → report
  //   The 'upload' step is shown whenever ANY data points are selected
  //   in summary preferences. It replaces per-document steps entirely.
  //   The processing dashboard, financial review, and report generation
  //   are handled within the upload → report transition (Phase 5+).
  //
  // Legacy flow (preserved for backward compatibility):
  //   summary → financial → metrics → credit-report → bank → corporate → report
  //   This flow activates only if the upload step hasn't been used.
  //
  // The decision is straightforward:
  //   - If prefs exist → concurrent flow (summary + upload + report)
  //   - If no prefs → required steps only (summary + report)
  // ---------------------------------------------------------------
  const availableSteps = useMemo(() => {
    const prefs = data.summaryPreferences?.selectedDataPoints;

    if (!prefs) {
      // Only show summary and report if no preferences yet
      return ALL_STEPS.filter((s) => s.required);
    }

    // Concurrent flow: summary → upload → report
    // The upload step is shown when any document types are selected.
    const hasAnyDataPoints =
      (prefs.financialStatements?.length ?? 0) > 0 ||
      (prefs.creditReport?.length ?? 0) > 0 ||
      (prefs.bankStatements?.length ?? 0) > 0 ||
      (prefs.corporateSearch?.length ?? 0) > 0 ||
      (prefs.metrics?.length ?? 0) > 0;

    if (hasAnyDataPoints) {
      const hasFinancial = (prefs.financialStatements?.length ?? 0) > 0 || (prefs.metrics?.length ?? 0) > 0;
      // Concurrent flow: summary → upload → review → metrics → generating
      // The 'report' step (required) is excluded; 'generating' replaces it as the terminal step.
      return ALL_STEPS.filter((step) => {
        if (step.id === 'report') return false;            // replaced by 'generating' in concurrent flow
        if (step.id === 'summary') return true;
        if (step.id === 'upload') return true;             // unified upload page
        if (step.id === 'overrides' && hasFinancial) return true; // financial overrides
        if (step.id === 'metrics' && hasFinancial) return true;   // metrics after overrides
        if (step.id === 'generating') return true;         // terminal step for concurrent flow
        return false;
      });
    }

    // No data points selected — only required steps
    return ALL_STEPS.filter((s) => s.required);
  }, [data.summaryPreferences]);

  // Current step index
  const stepIndex = useMemo(
    () => availableSteps.findIndex((s) => s.id === currentStep),
    [availableSteps, currentStep]
  );

  // Navigation helpers
  const canGoNext = stepIndex < availableSteps.length - 1;
  const canGoPrevious = stepIndex > 0;

  const setCurrentStep = useCallback((step: WorkflowStep) => {
    setCurrentStepState(step);
  }, []);

  const goToStep = useCallback(
    (step: WorkflowStep) => {
      const targetStep = availableSteps.find((s) => s.id === step) || ALL_STEPS.find((s) => s.id === step);
      
      if (targetStep) {
        setCurrentStep(step);
        navigate({ to: targetStep.path });
      }
    },
    [availableSteps, navigate, setCurrentStep]
  );

  const nextStep = useCallback(() => {
    if (canGoNext) {
      const nextStepConfig = availableSteps[stepIndex + 1];
      setCurrentStep(nextStepConfig.id);
      navigate({ to: nextStepConfig.path });
    }
  }, [canGoNext, availableSteps, stepIndex, navigate, setCurrentStep]);

  const previousStep = useCallback(() => {
    if (canGoPrevious) {
      const prevStepConfig = availableSteps[stepIndex - 1];
      setCurrentStep(prevStepConfig.id);
      navigate({ to: prevStepConfig.path });
    }
  }, [canGoPrevious, availableSteps, stepIndex, navigate, setCurrentStep]);

  const updateData = useCallback((updates: Partial<WorkflowData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    if (data.workflowId) {
      setWorkflowId(data.workflowId);
    }
  }, [data.workflowId]);

  const resetWorkflow = useCallback(() => {
    const freshData = getInitialData();
    // Clean up SSE and early extraction state
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setDocumentStatuses([]);
    setActivityLog([]);
    earlyExtractedRef.current = new Set();
    earlyExtractionsRef.current = [];
    earlyExtractionDoneRef.current = Promise.resolve();
    // set new workflow id
    setWorkflowId(freshData.workflowId);
    console.log('Workflow reset, new workflowId:', freshData.workflowId);
    storage?.removeItem(STORAGE_KEY);
    storage?.removeItem(STEP_STORAGE_KEY);
    storage?.removeItem(COMPLETED_STORAGE_KEY);

    setData(freshData);
    setCurrentStepState('summary');
    setCompletedSteps(new Set());

  }, []);

  const isStepComplete = useCallback(
    (step: WorkflowStep) => completedSteps.has(step),
    [completedSteps]
  );

  const markStepComplete = useCallback((step: WorkflowStep) => {
    setCompletedSteps((prev) => new Set([...prev, step]));
  }, []);

  const loadTemplate = useCallback((template: Template) => { 
  const updates = applyTemplateToWorkflow(template.config, data);

  setData((prev) => ({
    ...prev,
    ...updates,
    templateId: template.id, 
  }));

  return updates;
}, [data]);

  // Set resumed document statuses (called when loading a workflow from resume)
  const setResumedDocumentStatuses = useCallback((statuses: DocumentStatus[]) => {
    setDocumentStatuses(statuses);
  }, []);

  const value: WorkflowContextType = {
    currentStep,
    setCurrentStep,
    availableSteps,
    stepIndex,
    nextStep,
    previousStep,
    goToStep,
    canGoNext,
    canGoPrevious,
    data,
    updateData,
    resetWorkflow,
    loadTemplate,
    isStepComplete,
    completedSteps,
    markStepComplete,
    documentStatuses,
    setResumedDocumentStatuses,
    activityLog,
    addActivity,
    closeSse,
    waitForEarlyExtractions,
    reportResult,
    reportError,
  };

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
}

// ===========================================
// Hook
// ===========================================

export function useWorkflow() {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
}